/**
 * 📱 폰 PDF 미리보기 — 판정·계산 (v2.272.0)
 *
 * 왜: PDF 미리보기 창(components/PdfPreviewModal)은 iframe 에 **브라우저의 PDF 보기 기능**을 띄운다.
 *   그런데 안드로이드 크롬·삼성 인터넷·카카오톡 안 브라우저에는 그 기능이 없어 빈 칸(또는 「열기」 단추)만 보였고,
 *   아이폰·아이패드 사파리는 iframe 안 PDF 를 **첫 쪽만 그림처럼** 보여 넘겨 볼 수 없었다(사용자 10-17 「모바일에서는 PDF 미리보기가 안 보인다」).
 *   → 그런 기기에서만 pdf.js 로 쪽마다 화면에 직접 그린다(components/PdfCanvasView). PC 는 그대로 브라우저 PDF 보기(찾기·인쇄가 된다).
 *
 * 화면 부품과 떼어 둔 이유: 판정·계산을 그물(scripts/check-pdf-preview.ts)이 node 에서 그대로 부른다.
 */

/** 판정에 쓰는 navigator 의 일부 — 그물이 기기별 값을 꾸며 넣는다 */
export interface NavLike {
  userAgent: string;
  maxTouchPoints?: number;
  /** 브라우저가 PDF 를 스스로 그리는가(표준) — 안드로이드 크롬은 false */
  pdfViewerEnabled?: boolean;
}

/**
 * PDF 를 iframe 대신 pdf.js 로 그려야 하는 기기인가.
 *   ① 브라우저가 「PDF 를 못 그린다」고 말하면(pdfViewerEnabled === false) — 안드로이드 크롬·PC 에서 PDF 보기를 끈 크롬
 *   ② 안드로이드·아이폰·아이패드 — 말은 「그린다」여도 iframe 안에서는 못 그리거나 첫 쪽만 그린다
 *   ③ 아이패드(iPadOS 13+)는 「맥」이라고 말한다 → 손가락 터치 점이 여럿이면 아이패드로 본다
 */
export function needsCanvasPdf(nav: NavLike): boolean {
  if (nav.pdfViewerEnabled === false) return true;
  const ua = nav.userAgent || '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return true;
  return false;
}

/** 확대 단계 — 1 = 화면 폭에 맞춤. 폰에서 A4 글자가 작으니 2·3배까지 */
export const PDF_ZOOM_STEPS = [1, 1.5, 2, 3] as const;

export function nextZoom(cur: number, dir: 1 | -1): number {
  const steps = PDF_ZOOM_STEPS as readonly number[];
  const i = steps.indexOf(cur);
  if (i < 0) return 1;
  return steps[Math.min(steps.length - 1, Math.max(0, i + dir))]!;
}

/**
 * 쪽 그림(canvas)의 화소 배율 — 화면 배율(devicePixelRatio)만큼 촘촘히 그려야 글자가 선명한데,
 * 확대해서 그리면 쪽마다 수십 MB 가 된다(아이폰은 canvas 하나 1,670만 화소가 상한 · 전체 메모리도 작다).
 *   → 배율은 3(요즘 폰) 까지, 그리고 한 쪽이 MAX_PAGE_PIXELS 를 넘지 않게 줄인다(폭 맞춤 A4 는 넘지 않는다 · 2·3배 확대 때만 줄어든다).
 */
export const MAX_PAGE_PIXELS = 6_000_000;
export function pixelRatioFor(cssWidth: number, cssHeight: number, dpr: number): number {
  const want = Math.min(Math.max(dpr || 1, 1), 3);
  const area = cssWidth * cssHeight;
  if (area <= 0) return want;
  return Math.min(want, Math.sqrt(MAX_PAGE_PIXELS / area));
}

/**
 * 지금 보고 있는 쪽(1부터) — 각 쪽의 위쪽 끝(스크롤 상자 안 위치)과 지금 스크롤 위치로.
 * 화면 위에서 1/3 지점을 지난 마지막 쪽을 「보는 쪽」으로 친다.
 */
export function currentPageAt(pageTops: number[], scrollTop: number, viewHeight: number): number {
  const probe = scrollTop + viewHeight / 3;
  let cur = 1;
  for (let i = 0; i < pageTops.length; i++) if (pageTops[i]! <= probe) cur = i + 1;
  return cur;
}
